/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 25-07-2026
 *
 * Component voor het conditionele-tools-paneel linksonder in het modelcanvas (tot golf 4f een Custom
 * Element): de taglijst met view-toggles, de actieve condtag met de cond/cons-stateknoppen, en een
 * inline-editor voor het aanmaken, hernoemen en (met twee-klik-bevestiging) verwijderen van condtags.
 * Vervangt `#conditional` in canvas.html; de dialoog `condtagprompt.html` is hiermee vervallen.
 *
 * De state komt uit de `CondtagService`; elke klik gaat als actie naar de samenwerk-baas, die
 * samenwerking, undo, opslaan en log regelt.
 */
import {ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChild} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {BaseIslandComponent} from '../base-island.component';
import {CondtagService} from '../../services/condtag.service';
import {DlModel} from '../../app/model/dl-model';

/**
 * De stand van de inline-editor: een nieuwe tag, een bestaande tag in bewerking, of dicht.
 */
type EditorStand = {modus: 'nieuw'} | {modus: 'bewerk'; tag: string} | null;

/**
 * Het modelleerniveau vanaf waar condtags gemaakt mogen worden.
 */
const CONDTAG_NIVEAU = 50;

@Component({
    selector: 'dl-condtagpaneel',
    standalone: true,
    templateUrl: './dl-condtagpaneel.component.html',
    styleUrls: ['./dl-condtagpaneel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, NgTemplateOutlet],
    host: {
        '[class.dl-condtagpaneel--zichtbaar]': 'zichtbaar()',
        '[class.dl-condtagpaneel--open]': 'condtagService.open()',
        '[class.dl-condtagpaneel--tagged]': '!!condtagService.condtag()',
        '[class.dl-condtagpaneel--met-normbalk]': 'metNormbalk()',
        '(click)': 'ontwapen()'
    }
})
export class DlCondtagpaneelComponent extends BaseIslandComponent {

    // --- Properties ---

    /**
     * De condtag-sessie-state en de actie-callbacks naar de samenwerk-baas.
     */
    protected readonly condtagService = inject(CondtagService);

    /**
     * Het model, voor het effectieve modelleerniveau en het normmodel.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De stand van de inline-editor.
     */
    protected readonly editor = signal<EditorStand>(null);

    /**
     * De huidige invoer in de editor.
     */
    protected readonly naamInvoer = signal('');

    /**
     * De tag waarvan de verwijderknop gewapend is (eerste klik gezet, tweede klik verwijdert).
     */
    protected readonly verwijderKandidaat = signal<string | null>(null);

    /**
     * Het invoerveld van de editor, voor focus bij openen.
     */
    private readonly naamVeld = viewChild<ElementRef<HTMLInputElement>>('naamVeld');

    // --- Afgeleide state ---

    /**
     * Het paneel staat er zodra condtags gemaakt mogen worden of het model ze al heeft.
     */
    protected readonly zichtbaar = computed(() =>
        this.dlModel.effectief_modelleerniveau >= CONDTAG_NIVEAU || this.condtagService.heeftCondtags());

    /**
     * Of er nieuwe condtags gemaakt mogen worden (de plusknop).
     */
    protected readonly magNieuweTag = computed(() => this.dlModel.effectief_modelleerniveau >= CONDTAG_NIVEAU);

    /**
     * Het paneel schuift omhoog wanneer de normbalk onderin staat.
     */
    protected readonly metNormbalk = computed(() => !!this.dlModel.norm);

    /**
     * De genormaliseerde editor-invoer: condtagnamen zijn altijd lowercase.
     */
    protected readonly genormaliseerdeNaam = computed(() => this.naamInvoer().trim().toLowerCase());

    /**
     * Of de ingevoerde naam al bestaat als andere tag; de invoer kleurt dan rood en de editor
     * blijft open.
     */
    protected readonly naamIsDuplicaat = computed(() => {
        const naam = this.genormaliseerdeNaam();
        const editor = this.editor();
        if (!naam || !editor) {
            return false;
        }
        if (editor.modus === 'bewerk' && naam === editor.tag) {
            return false;
        }
        return this.condtagService.conditioneleTags().hasOwnProperty(naam);
    });

    /**
     * Of de editor-invoer bewaard mag worden.
     */
    protected readonly magBewaren = computed(() => !!this.genormaliseerdeNaam() && !this.naamIsDuplicaat());

    constructor() {
        super();
    }

    // --- Protected methods (template-acties) ---

    /**
     * Open of sluit het paneel. Lokaal gedrag: dit gaat niet over de lijn (sinds 09-09-2026).
     */
    protected klikToggleOpen(): void {
        this.sluitEditor();
        this.condtagService.toggleOpen(!this.condtagService.open());
    }

    /**
     * Maak een tag de actieve condtag; de modelactie (`zetConditieset`) sluit daarbij het paneel.
     */
    protected klikTag(tag: string): void {
        this.sluitEditor();
        this.condtagService.kiesTag(tag);
    }

    /**
     * Of deze tag momenteel in de inline-editor bewerkt wordt.
     */
    protected isInBewerk(tag: string): boolean {
        const editor = this.editor();
        return editor?.modus === 'bewerk' && editor.tag === tag;
    }

    /**
     * Toggle de zichtbaarheid van de elementen van een tag; de samenwerk-baas negeert dit voor de actieve tag.
     */
    protected klikView(tag: string): void {
        this.condtagService.toggleView(tag);
    }

    /**
     * Deselecteer de actieve condtag (de X-knop).
     */
    protected klikUnset(): void {
        this.condtagService.unsetTag();
    }

    /**
     * Zet de state van de actieve condtag op conditie (?) of consequentie (!).
     */
    protected klikState(state: 'cond' | 'cons'): void {
        this.condtagService.zetState(state);
    }

    /**
     * De plusknop: open zo nodig het paneel en toon de lege editor-rij bovenaan de lijst.
     */
    protected startNieuw(): void {
        if (!this.condtagService.open()) {
            this.condtagService.toggleOpen(true);
        }
        this.openEditor({modus: 'nieuw'}, '');
    }

    /**
     * Het potloodje: vervang het taglabel door de editor met de huidige naam.
     */
    protected startBewerk(tag: string): void {
        this.openEditor({modus: 'bewerk', tag}, tag);
    }

    /**
     * Bewaar de editor-invoer: hernoem of maak aan. Een ongewijzigde naam sluit alleen de
     * editor (geen loze undo-/logactie); een duplicaat blijft open met rode invoer.
     */
    protected bewaarEditor(): void {
        const editor = this.editor();
        const naam = this.genormaliseerdeNaam();
        if (!editor || !this.magBewaren()) {
            return;
        }
        switch (editor.modus) {
            case 'nieuw':
                this.condtagService.nieuweTag(naam);
                break;
            case 'bewerk':
                if (naam !== editor.tag) {
                    this.condtagService.wijzigTag(editor.tag, naam);
                }
                break;
        }
        this.sluitEditor();
    }

    /**
     * Sluit de editor zonder iets te bewaren.
     */
    protected annuleerEditor(): void {
        this.sluitEditor();
    }

    /**
     * De prullenbak: eerste klik wapent, tweede klik verwijdert de tag inclusief elementen.
     * stopPropagation zodat de host-click de wapening niet direct weer opheft.
     */
    protected klikVerwijder(tag: string, event: Event): void {
        event.stopPropagation();
        this.sluitEditor(false);
        if (this.verwijderKandidaat() === tag) {
            this.verwijderKandidaat.set(null);
            this.condtagService.verwijderTag(tag);
        } else {
            this.verwijderKandidaat.set(tag);
        }
    }

    /**
     * Elke klik elders in het paneel ontwapent een gewapende prullenbak.
     */
    protected ontwapen(): void {
        this.verwijderKandidaat.set(null);
    }

    // --- Private methods ---

    /**
     * Open de editor in de gegeven stand en zet de focus op het invoerveld zodra het er is.
     */
    private openEditor(stand: EditorStand, naam: string): void {
        this.verwijderKandidaat.set(null);
        this.editor.set(stand);
        this.naamInvoer.set(naam);
        setTimeout(() => this.naamVeld()?.nativeElement.focus());
    }

    /**
     * Sluit de editor en (standaard) ontwapen ook de prullenbak.
     */
    private sluitEditor(ookOntwapenen = true): void {
        this.editor.set(null);
        this.naamInvoer.set('');
        if (ookOntwapenen) {
            this.verwijderKandidaat.set(null);
        }
    }
}
